// Platform storage limits, not a promise of Google's displayed snippet length.
export const SEO_TITLE_MAX_LENGTH = 70;
export const SEO_DESCRIPTION_MAX_LENGTH = 180;
export const seoFieldLimits = { seoTitle: SEO_TITLE_MAX_LENGTH, seoDescription: SEO_DESCRIPTION_MAX_LENGTH } as const;
export type SeoField = keyof typeof seoFieldLimits;

/** Fit generated metadata only; valid short strings are deliberately unchanged.
 * Match Zod's UTF-16 length limit without cutting a Unicode grapheme in half. */
export function fitGeneratedSeoText(value: string, limit: number): string {
  if (value.length <= limit) return value;
  const text = value.normalize("NFC").replace(/\s+/gu, " ").trim();
  if (text.length <= limit) return text;
  let prefix = "";
  for (const { segment } of new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(text)) {
    if (prefix.length + segment.length > limit) break;
    prefix += segment;
  }
  // Prefer the previous word boundary, unless it discards most of the title.
  if (!/\s$/u.test(prefix) && !/^[\s.,;:!?|/)-]/u.test(text.slice(prefix.length))) {
    const boundary = prefix.lastIndexOf(" ");
    if (boundary >= Math.floor(limit * 0.65)) prefix = prefix.slice(0, boundary);
  }
  return prefix.trimEnd().replace(/[\s|,;:\-–—]+$/u, "");
}

/** Do not coerce types, remove unknown keys, repair slugs or touch page content. */
export function fitGeneratedPageSeo(input: Record<string, unknown>): { data: Record<string, unknown>; adjusted: SeoField[] } {
  const data = { ...input }, adjusted: SeoField[] = [];
  for (const field of Object.keys(seoFieldLimits) as SeoField[]) {
    const value = input[field];
    if (typeof value !== "string" || value.length <= seoFieldLimits[field]) continue;
    const fitted = fitGeneratedSeoText(value, seoFieldLimits[field]);
    if (fitted !== value) { data[field] = fitted; adjusted.push(field); }
  }
  return { data, adjusted };
}
